import { prettifyToken } from './chunk-5S6LGIZ3.js';
import { protocolDisplayName, protocolCategory } from './chunk-5TLPPHSP.js';
import { RiskLevel } from './chunk-6B5KO6TH.js';
import { safeChainText, formatUsd, SUB_CENT_USD, subCentUsdBound } from './chunk-V7TVGBSG.js';
import { globalSingleton } from './chunk-N7SZ66QO.js';

// src/defi/protocol.ts
var ProtocolCategory = {
  LENDING: "lending",
  DEX: "dex",
  LIQUIDITY: "liquidity",
  STAKING: "staking",
  YIELD: "yield",
  DERIVATIVES: "derivatives",
  PERPS: "perps",
  BRIDGE: "bridge",
  RWA: "rwa",
  DOMAINS: "domains",
  SOCIALFI: "socialfi",
  GENERIC: "generic"
};
var ProtocolId = {
  AAVE_V2: "aave-v2",
  AAVE_V3: "aave-v3",
  COMPOUND_V2: "compound-v2",
  COMPOUND_V3: "compound-v3",
  MAKER: "makerdao",
  UNISWAP_V2: "uniswap-v2",
  UNISWAP_V3: "uniswap-v3",
  CURVE: "curve",
  BALANCER: "balancer",
  LIDO: "lido",
  ROCKET_POOL: "rocket-pool",
  CONVEX: "convex",
  YEARN: "yearn",
  GMX: "gmx",
  PENDLE: "pendle",
  EIGENLAYER: "eigenlayer"
};
var SEED = [
  { id: ProtocolId.AAVE_V3, name: "Aave v3", category: ProtocolCategory.LENDING, brandColor: "#B6509E", website: "https://aave.com" },
  { id: ProtocolId.AAVE_V2, name: "Aave v2", category: ProtocolCategory.LENDING, brandColor: "#B6509E" },
  { id: ProtocolId.COMPOUND_V3, name: "Compound v3", category: ProtocolCategory.LENDING, brandColor: "#00D395", website: "https://compound.finance" },
  { id: ProtocolId.COMPOUND_V2, name: "Compound v2", category: ProtocolCategory.LENDING, brandColor: "#00D395" },
  { id: ProtocolId.MAKER, name: "MakerDAO", category: ProtocolCategory.LENDING, brandColor: "#1AAB9B" },
  { id: ProtocolId.UNISWAP_V3, name: "Uniswap v3", category: ProtocolCategory.DEX, brandColor: "#FF007A", website: "https://uniswap.org" },
  { id: ProtocolId.UNISWAP_V2, name: "Uniswap v2", category: ProtocolCategory.DEX, brandColor: "#FF007A" },
  { id: ProtocolId.CURVE, name: "Curve", category: ProtocolCategory.DEX, brandColor: "#A5D8FF" },
  { id: ProtocolId.BALANCER, name: "Balancer", category: ProtocolCategory.DEX, brandColor: "#1E1E1E" },
  { id: ProtocolId.LIDO, name: "Lido", category: ProtocolCategory.STAKING, brandColor: "#00A3FF", website: "https://lido.fi" },
  { id: ProtocolId.ROCKET_POOL, name: "Rocket Pool", category: ProtocolCategory.STAKING, brandColor: "#FF6B4A" },
  { id: ProtocolId.CONVEX, name: "Convex", category: ProtocolCategory.YIELD, brandColor: "#3A3A3A" },
  { id: ProtocolId.YEARN, name: "Yearn", category: ProtocolCategory.YIELD, brandColor: "#0657F9" },
  { id: ProtocolId.GMX, name: "GMX", category: ProtocolCategory.PERPS, brandColor: "#2D42FC" },
  { id: ProtocolId.PENDLE, name: "Pendle", category: ProtocolCategory.YIELD, brandColor: "#1FC7D4" },
  { id: ProtocolId.EIGENLAYER, name: "EigenLayer", category: ProtocolCategory.STAKING, brandColor: "#1A0C6D" }
];
var protocols = globalSingleton("defi/protocols", () => ({
  registry: new Map(SEED.map((m) => [m.id, m])),
  catalogMetas: /* @__PURE__ */ new Map()
}));
var PROTOCOL_LOGO_CDN = "https://icons.llamao.fi/icons/protocols/";
var CATALOG_CATEGORY = {
  exchange: ProtocolCategory.DEX,
  aggregator: ProtocolCategory.DEX,
  lending: ProtocolCategory.LENDING,
  flashloan: ProtocolCategory.LENDING,
  staking: ProtocolCategory.STAKING,
  derivatives: ProtocolCategory.DERIVATIVES,
  bridge: ProtocolCategory.BRIDGE
};
function catalogMeta(id) {
  const cached = protocols.catalogMetas.get(id);
  if (cached !== void 0) return cached ?? void 0;
  const lower = id.trim().toLowerCase();
  const key = [lower, lower.replace(/-/g, "_")].find((candidate) => protocolDisplayName(candidate));
  const category = key ? protocolCategory(key) : void 0;
  const meta = key ? {
    id,
    name: protocolDisplayName(key),
    category: (category && CATALOG_CATEGORY[category]) ?? ProtocolCategory.GENERIC
  } : null;
  protocols.catalogMetas.set(id, meta);
  return meta ?? void 0;
}
function getProtocolMeta(id) {
  if (!id) return void 0;
  return protocols.registry.get(id) ?? protocols.registry.get(id.toLowerCase()) ?? catalogMeta(id);
}
function protocolLabel(position) {
  const id = position?.protocolId ?? "";
  const registered = getProtocolMeta(id)?.name;
  if (registered) return registered;
  const provider = position?.metadata?.protocol_name;
  const named = typeof provider === "string" ? safeChainText(provider, { maxChars: 40 }) : "";
  return named || (id ? prettifyToken(id) : "");
}
function registerProtocols(metas) {
  for (const m of metas) protocols.registry.set(m.id, m);
}
function allProtocols() {
  return [...protocols.registry.values()];
}
function resolveProtocolLogo(id, opts = {}) {
  const meta = getProtocolMeta(id);
  const fromResolver = opts.resolver?.(id, meta);
  if (fromResolver) return fromResolver;
  const base = opts.cdnBase ?? PROTOCOL_LOGO_CDN;
  if (meta?.logoUrl) {
    return /^https?:\/\//.test(meta.logoUrl) ? meta.logoUrl : `${base}${meta.logoUrl}`;
  }
  return `${base}${id}.jpg`;
}

// src/defi/position.ts
var PROTOCOL_VIEW_ENRICHMENT = "protocol_view";
var PositionKind = {
  SUPPLY: "supply",
  BORROW: "borrow",
  STAKE: "stake",
  LIQUIDITY: "liquidity",
  FARM: "farm",
  VAULT: "vault",
  PERP: "perp",
  OPTION: "option",
  LOCKED: "locked",
  OTHER: "other"
};
function positionKey(p) {
  return `${p.chainId.toLowerCase()}:${p.protocolId}:${p.id}`;
}
function netPositionUsd(p) {
  if (p.netUsd != null) return p.netUsd;
  if (p.collateralUsd != null || p.debtUsd != null) {
    return (p.collateralUsd ?? 0) - (p.debtUsd ?? 0);
  }
  return p.tvlUsd ?? null;
}
var BARE_KIND_LABEL = /^(supply|supplied|supplying|borrow|borrowed|borrowing|debt|loan|lend|lending|lent|deposit|deposited|stake|staked|staking|liquidity|lp|pool|farm|farming|yield|vault|position|other)$/;
function isBareKindLabel(label) {
  const l = (label ?? "").trim().toLowerCase();
  if (!l) return true;
  return BARE_KIND_LABEL.test(l);
}
function positionAssetSymbols(p) {
  const legs = [...p.supplied ?? [], ...p.borrowed ?? [], ...p.rewards ?? []];
  const byValue = [...legs].sort((a, b) => (b.usdValue ?? 0) - (a.usdValue ?? 0));
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const leg of byValue) {
    const symbol = (leg.symbol ?? "").trim();
    if (!symbol || symbol === "?") continue;
    const canonical = symbol.replace(/^[ac]([A-Z])/, "$1").toLowerCase();
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    out.push(symbol);
  }
  return out;
}

// src/defi/metrics.ts
function healthFactorLevel(hf) {
  if (hf == null || !Number.isFinite(hf)) return void 0;
  if (hf < 1) return RiskLevel.SEVERE;
  if (hf < 1.1) return RiskLevel.HIGH;
  if (hf < 1.5) return RiskLevel.MEDIUM;
  if (hf < 2) return RiskLevel.LOW;
  return RiskLevel.SAFE;
}
function formatHealthFactor(hf) {
  if (hf == null || !Number.isFinite(hf)) return "\u221E";
  return hf.toFixed(2);
}
function precisionBound(digits) {
  return digits <= 0 ? "1" : `0.${"0".repeat(digits - 1)}1`;
}
function roundedToNothing(body) {
  return /^0(\.0+)?$/.test(body);
}
function formatApy(apy, opts = {}) {
  if (apy == null || !Number.isFinite(apy)) return "\u2014";
  const digits = opts.maxFractionDigits ?? 2;
  const magnitude = Math.abs(apy).toFixed(digits);
  if (apy !== 0 && roundedToNothing(magnitude)) {
    const bound = `${precisionBound(digits)}%`;
    return apy < 0 ? `>-${bound}` : `<${bound}`;
  }
  const body = `${magnitude}%`;
  return apy < 0 ? `-${body}` : body;
}
function formatUsdDelta(value) {
  if (value == null || !Number.isFinite(value)) return "\u2014";
  if (value === 0) return formatUsd(0);
  if (Math.abs(value) < SUB_CENT_USD) return subCentUsdBound(value);
  const body = formatUsd(Math.abs(value));
  return value > 0 ? `+${body}` : `-${body}`;
}

export { PROTOCOL_LOGO_CDN, PROTOCOL_VIEW_ENRICHMENT, PositionKind, ProtocolCategory, ProtocolId, allProtocols, formatApy, formatHealthFactor, formatUsdDelta, getProtocolMeta, healthFactorLevel, isBareKindLabel, netPositionUsd, positionAssetSymbols, positionKey, protocolLabel, registerProtocols, resolveProtocolLogo };
//# sourceMappingURL=chunk-4AENTCMR.js.map
//# sourceMappingURL=chunk-4AENTCMR.js.map